/**
 * views/loadViews.ts —— 视图声明装载与闭集校验（U-923 布局解析入口）。
 * 纪律：视图声明只描述布局与槽——出现取数/连接类键 ⇒ 第二投影，红；
 *      九类槽闭集／可见性三值／交互态六态缺一越一即红（U-930 §2/§3/§4）。
 *
 * ★F-01／F-10（第二真值源消灭）：**声明真源＝后端 `src/sononux/decl/views.yaml`**
 *   （单一源，经 `./declViews.ts` 构建期读入）；本目录**不再持** `views/*.yaml` 在盘声明。
 *   平台位件＝真源 `views` 节里除「归属名」外的全部件；归属名（`sessions` 等 `UI_VIEWS` 成员）
 *   只作读面归属，不占视图环 tab（见 `views/declViews.ts` 与后端 `ui_view_map.UI_VIEWS`）。
 */
import yaml from "js-yaml";
import {
  SLOT_KINDS, VERDICTS, VIEW_STATES, VISIBILITIES,
  type FieldDecl, type PanelDecl, type SlotDecl, type ViewDecl,
} from "./schema";
import { E_CODES } from "../transport/errorText";
import { loadDeclViews, ViewDeclarationError } from "./declViews";

/** 归属名（**不占视图环 tab**；与后端 `runtime/entry/ui_view_map.py::UI_VIEWS` 之外的归属口径一致） */
export const ATTRIBUTION_ONLY_VIEWS: readonly string[] = ["sessions"];

/**
 * ★L-PF-B3 装载腿换轴：构建期聚合 → **平台位声明 ∪ 运行期登记**（账行逐字）。
 * · `RAW_FAMILY` ＝**平台位声明**（键＝视图名；值＝声明件原文；**唯一来源＝后端 decl/views.yaml**）；
 * · `RUNTIME_REGISTRY` ＝**运行期登记面**（登记项来自 B2 的帧／能力自带声明）；
 * · 装载族 ＝ 两者之并（键＝视图名；登记项越平台位／重复登记 ⇒ 拒，闭集校验同 `validateViewDecl`）。
 */
const RAW_FAMILY: Record<string, string> = (() => {
  const family: Record<string, string> = {};
  for (const doc of loadDeclViews()) {
    if (ATTRIBUTION_ONLY_VIEWS.includes(doc.name)) continue;   // 归属名不计入视图族
    family[doc.name] = doc.raw;
  }
  return family;
})();

/** 全族声明原文（含归属名；供归属面核对与断言） */
export const ALL_DECL_VIEWS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(loadDeclViews().map((d) => [d.name, d.raw])),
);

/** 运行期登记面（B3：键＝视图名，值＝声明件原文；只增不改平台位） */
const RUNTIME_REGISTRY = new Map<string, string>();

/** 运行期登记：能力装载 ⇒ 其视图声明入面（越平台位／重复登记 ⇒ 拒；先闭集校验后入面） */
export function registerViewDeclaration(name: string, raw: string): void {
  if (Object.prototype.hasOwnProperty.call(RAW_FAMILY, name)) {
    throw new ViewDeclarationError(name + "：运行期登记不得越平台位声明");
  }
  if (RUNTIME_REGISTRY.has(name)) {
    throw new ViewDeclarationError(name + "：运行期登记重复 " + name);
  }
  validateViewDecl(yaml.load(raw), name);
  RUNTIME_REGISTRY.set(name, raw);
}

/** 运行期登记退场（能力卸载 ⇒ 其条目消失；返回是否确有登记项被撤） */
export function unregisterViewDeclaration(name: string): boolean {
  return RUNTIME_REGISTRY.delete(name);
}

/** 已登记视图名（升序；供归面依据与断言消费） */
export function registeredViewNames(): string[] {
  return [...RUNTIME_REGISTRY.keys()].sort();
}

/** 禁取数键（视图声明含取数/连接语义 ⇒ 第二投影，红） */
const FORBIDDEN_KEYS = new Set([
  "sql", "query", "fetch", "url", "db", "table", "dsn", "connection", "endpoint",
]);

/** 错误类**定义位在 `./declViews`**（避免循环 import）；此处再导出保持既有导入面 */
export { ViewDeclarationError };

/** 装载全视图族（平台位声明 ∪ 运行期登记；键＝视图名） */
export function loadViewFamily(): Record<string, ViewDecl> {
  const family: Record<string, ViewDecl> = {};
  for (const [name, raw] of Object.entries(RAW_FAMILY)) {
    family[name] = validateViewDecl(yaml.load(raw), name);
  }
  for (const [name, raw] of RUNTIME_REGISTRY) {
    family[name] = validateViewDecl(yaml.load(raw), name);
  }
  return family;
}

/** 逐件校验（形状＋闭集＋引用完整性；越集即抛） */
export function validateViewDecl(raw: unknown, name: string): ViewDecl {
  assertNoForbiddenKeys(raw, name + " 根");
  const doc = raw as Partial<ViewDecl>;
  if (!doc || typeof doc !== "object") throw new ViewDeclarationError(name + "：非对象");
  if (doc.name !== name) throw new ViewDeclarationError(name + "：name 与文件名不符");
  if (typeof doc.title !== "string" || typeof doc.layout_version !== "string") {
    throw new ViewDeclarationError(name + "：title/layout_version 缺失");
  }
  const slots = validateSlots(doc, name);
  validatePanels(doc, name, slots);
  validateNameCollisions(doc, name, slots);   // ★20261005（SAME 报告 A 类）：视图名 × 槽/面板 id 撞名 ⇒ 装载期直接拒
  validateFields(doc, name, slots);
  validateStates(doc, name);
  validateDecisions(doc, name, slots);
  return doc as ViewDecl;
}

function validateSlots(doc: Partial<ViewDecl>, name: string): SlotDecl[] {
  const slots = doc.slots ?? [];
  if (slots.length === 0) throw new ViewDeclarationError(name + "：slots 空");
  const ids = new Set<string>();
  for (const slot of slots) {
    if (ids.has(slot.id)) throw new ViewDeclarationError(name + "：槽 id 重复 " + slot.id);
    ids.add(slot.id);
    if (!SLOT_KINDS.includes(slot.kind)) {
      throw new ViewDeclarationError(name + "：槽 kind 越出九类闭集 " + slot.kind);
    }
    if (typeof slot.component !== "string" || !slot.component) {
      throw new ViewDeclarationError(name + "：槽组件缺失 " + slot.id);
    }
    if (!Array.isArray(slot.fields)) {
      throw new ViewDeclarationError(name + "：槽 fields 非数组 " + slot.id);
    }
  }
  return slots;
}

/**
 * ★20261005（SAME 报告 A 类 + detail 真缺陷的**同型重犯闸**）：
 * `a2ui.ts::checkNode` 的锚解析序＝**槽 → 面板 → 视图名**，故「视图名 ≡ 某槽 id」时，
 * 根节点（type=XLayout，source.slot=视图名）会被锚成该槽的组件 ⇒ 实渲染抛 `E_UI_STRUCTURE`、**整视图零输出**。
 * 原 `validateSlots/validatePanels` 只查「槽内重复」「面板 vs 槽」，**不比对视图名** ⇒ 这类病只能靠命名巧合不发生。
 * 本闸把该判据前移到**装载期**：撞名 ⇒ 直接拒（不等到渲染）。
 */
function validateNameCollisions(doc: Partial<ViewDecl>, name: string, slots: SlotDecl[]): void {
  const panelIds = Array.isArray(doc.panels) ? doc.panels.map((x) => String((x as { id?: unknown }).id ?? "")) : [];
  const clash = [...slots.map((s) => s.id), ...panelIds].find((id) => id !== "" && id === name);
  if (clash) {
    throw new ViewDeclarationError(
      name + "：视图名与槽/面板 id 撞名（锚解析 slot 优先 ⇒ 根节点必被锚错、渲染必抛）：" + clash,
    );
  }
}

function validatePanels(doc: Partial<ViewDecl>, name: string, slots: SlotDecl[]): void {
  const panels: PanelDecl[] = doc.panels ?? [];
  if (panels.length === 0) throw new ViewDeclarationError(name + "：panels 空");
  const ids = new Set<string>();
  const slotIds = new Set(slots.map((s) => s.id));
  for (const panel of panels) {
    if (ids.has(panel.id) || slotIds.has(panel.id)) {
      throw new ViewDeclarationError(name + "：面板 id 重复/与槽撞名 " + panel.id);
    }
    ids.add(panel.id);
    for (const slotId of panel.slots ?? []) {
      if (!slotIds.has(slotId)) {
        throw new ViewDeclarationError(name + "：面板引未声明槽 " + slotId);
      }
    }
  }
}

function validateFields(doc: Partial<ViewDecl>, name: string, slots: SlotDecl[]): void {
  const fields: FieldDecl[] = doc.fields ?? [];
  const keys = new Set<string>();
  for (const field of fields) {
    if (keys.has(field.key)) throw new ViewDeclarationError(name + "：字段重复 " + field.key);
    keys.add(field.key);
    if (!VISIBILITIES.includes(field.visibility)) {
      throw new ViewDeclarationError(name + "：可见性越出三值闭集 " + field.key);
    }
    if (typeof field.readonly !== "boolean") {
      throw new ViewDeclarationError(name + "：readonly 非布尔 " + field.key);
    }
  }
  for (const slot of slots) {
    for (const key of slot.fields) {
      const field = fields.find((f) => f.key === key);
      if (!field) throw new ViewDeclarationError(name + "：出现未声明字段 " + key);
    }
  }
  for (const field of fields) {
    const listed = slots.filter((s) => s.fields.includes(field.key));
    if (!listed.some((s) => s.id === field.placement)) {
      throw new ViewDeclarationError(name + "：字段摆放未落在引用槽 " + field.key);
    }
  }
}

function validateStates(doc: Partial<ViewDecl>, name: string): void {
  const states = (doc.states ?? []) as string[];
  const expected = [...VIEW_STATES].sort().join(",");
  const actual = [...states].sort().join(",");
  if (actual !== expected) {
    throw new ViewDeclarationError(name + "：交互态非六态闭集（缺一即漂移）");
  }
}

function validateDecisions(doc: Partial<ViewDecl>, name: string, slots: SlotDecl[]): void {
  const decisions = doc.decisions;
  if (!decisions) return;
  if (!slots.some((s) => s.id === decisions.slot)) {
    throw new ViewDeclarationError(name + "：决策槽未声明 " + decisions.slot);
  }
  for (const option of decisions.options ?? []) {
    const verdict = decisions.verdicts?.[option];
    if (!VERDICTS.includes(verdict)) {
      throw new ViewDeclarationError(name + "：决策档越出三档 " + option);
    }
  }
  if (typeof decisions.must_ledger !== "boolean") {
    throw new ViewDeclarationError(name + "：must_ledger 非布尔");
  }
}

function assertNoForbiddenKeys(node: unknown, where: string): void {
  if (Array.isArray(node)) {
    node.forEach((item, i) => assertNoForbiddenKeys(item, where + "[" + i + "]"));
    return;
  }
  if (!node || typeof node !== "object") return;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (FORBIDDEN_KEYS.has(key)) {
      throw new ViewDeclarationError(where + "：禁取数键 " + key);
    }
    assertNoForbiddenKeys(value, where + "." + key);
  }
}
